import { Dispatch, MouseEvent, SetStateAction, useCallback, useEffect } from 'react';
import { cssVar, HStack, useToast, Button } from '@cad-arc';
import { EventPanelFormType } from '@mark43/cad-api';
import { useLocation, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios-cad-nx';
import { FR_CREATE_EVENT_SUB_PATH, FR_EVENT_ROOT_PATH, ROOT_PATH } from '../router/constants';
import { FrPageFooter } from '../fr-page-layout/fr-page-footer';
import { useAddVehicleToForm, useAddPersonToForm } from './hooks';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';
import { FormCancelButton } from '../forms/integrated-form-components/form-cancel-button';
import { useIntegratedForm } from '../forms/use-integrated-form';
import { useSubmitEventForm } from '../common/hooks/use-submit-event-form';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { ResponsiveButtons } from '../common/components/responsive-buttons';
import { buttonBreakpoints } from './constants';

export function FrCreateOrEditEventFooter({
    setUserManuallyAddedVehicleField,
    setUserManuallyAddedPersonField,
}: {
    setUserManuallyAddedVehicleField: Dispatch<SetStateAction<boolean>>;
    setUserManuallyAddedPersonField: Dispatch<SetStateAction<boolean>>;
}) {
    const navigate = useNavigate();
    const { form, isSubmitting } = useIntegratedForm<EventPanelFormType>();
    const addVehicleToForm = useAddVehicleToForm();
    const addPersonToForm = useAddPersonToForm();
    const submitEventForm = useSubmitEventForm();
    const location = useLocation();
    const unitAssignment = useUnitAssignment();
    const isCreateEventForm = location.pathname.includes(
        `${FR_EVENT_ROOT_PATH}/${FR_CREATE_EVENT_SUB_PATH}`
    );
    const toast = useToast();

    useEffect(
        () => {
            if (location?.state?.shouldBeSaved) {
                saveEvent();
                navigate(location.pathname, { replace: true }); // This clears the state but stays on the same page
            }
        }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [location?.state?.shouldBeSaved]
    );

    const onCancel = useCallback(() => {
        form.reset();
        navigate(ROOT_PATH);
    }, [navigate, form]);

    const saveEvent = (clickEvent?: MouseEvent<HTMLButtonElement>) => {
        if (isCreateEventForm && unitAssignment?.unitId) {
            form.setValue('selfInitiateUnitId', unitAssignment.unitId);
        }

        submitEventForm(
            clickEvent,
            () => {
                form.reset();
                navigate(ROOT_PATH, {
                    state: {
                        previousLocationPathname: location.pathname,
                    },
                    replace: true,
                });
            },
            (error) => {
                if (error instanceof AxiosError && error?.response?.data?.error) {
                    toast({
                        description: error.response.data.error,
                        status: 'error',
                    });
                }
                if (location?.state?.shouldBeSaved) {
                    navigate(location.pathname, { replace: true }); // This clears the state but stays on the same page
                }
            }
        );
    };

    return (
        <FrPageFooter>
            <HStack
                margin={0}
                padding={cssVar('arc.space.2')}
                alignItems="center"
                justifyContent="space-between"
                width="100%"
            >
                <FormCancelButton size="xl" onClick={onCancel} isDisabled={isSubmitting}>
                    Cancel
                </FormCancelButton>
                <ResponsiveButtons
                    moreActionsButtonProps={{ size: 'xl' }}
                    containerProps={{
                        margin: 0,
                        marginLeft: 'auto',
                        padding: 0,
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                    }}
                    buttons={[
                        {
                            breakpoint: buttonBreakpoints.ADD_TOW_VEHICLE,
                            component: (
                                <Button
                                    size="xl"
                                    leadingVisual="Add"
                                    isDisabled={isSubmitting}
                                    onClick={() => {
                                        addVehicleToForm('towVehicles');
                                        setUserManuallyAddedVehicleField(true);
                                    }}
                                >
                                    Tow Vehicle
                                </Button>
                            ),
                        },
                        {
                            breakpoint: buttonBreakpoints.ADD_VEHICLE,
                            component: (
                                <Button
                                    size="xl"
                                    leadingVisual="Add"
                                    isDisabled={isSubmitting}
                                    onClick={() => {
                                        addVehicleToForm('vehicles');
                                        setUserManuallyAddedVehicleField(true);
                                    }}
                                >
                                    Vehicle
                                </Button>
                            ),
                        },
                        {
                            breakpoint: buttonBreakpoints.ADD_INVOLVED_PERSON,
                            component: (
                                <Button
                                    size="xl"
                                    leadingVisual="Add"
                                    isDisabled={isSubmitting}
                                    onClick={() => {
                                        addPersonToForm('involvedPersons');
                                        setUserManuallyAddedPersonField(true);
                                    }}
                                >
                                    Involved Person
                                </Button>
                            ),
                        },
                        {
                            breakpoint: buttonBreakpoints.ADD_REPORTING_PARTY,
                            component: (
                                <Button
                                    size="xl"
                                    leadingVisual="Add"
                                    isDisabled={isSubmitting}
                                    onClick={() => {
                                        addPersonToForm('reportingParties');
                                        setUserManuallyAddedPersonField(true);
                                    }}
                                >
                                    Reporting Party
                                </Button>
                            ),
                        },
                    ]}
                />
                <FormSaveButton size="xl" onClick={saveEvent}>
                    Save
                </FormSaveButton>
            </HStack>
        </FrPageFooter>
    );
}
